// Scan configuration + execution tab. Shows the config form when idle,
// swaps to LiveProgress when a scan is running or just got enqueued.

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import { ApiError } from "@/lib/api";
import { useExecuteScan } from "@/hooks/useEngagementDetail";
import { usePlan } from "@/hooks/usePlan";
import type { Engagement } from "@/types/engagement";
import type { ScanConfig } from "@/types/engagement-detail";
import { LiveProgress } from "./LiveProgress";

interface ScanTabProps {
  engagement: Engagement;
}

const PORT_MODE_KEYS: ScanConfig["portMode"][] = ["common", "extended", "full"];

// Each row: [seconds, label, minimum plan tier that unlocks it].
// The plan column is shown to the right of locked rows so users see what
// they need to upgrade to (matches the PORT SCAN MODE pattern). The
// 5-min row is always available so its plan column is empty. The plan
// caps come from app/services/pricing.py: free=300, basic=1800, pro=3600,
// enterprise=7200.
const TIMEOUTS: Array<[number, string]> = [
  [300, ""],
  [600, "BASIC+"],
  [1800, "BASIC+"],
  [3600, "PRO+"],
  [7200, "ENTERPRISE"],
];

export function ScanTab({ engagement }: ScanTabProps) {
  const t = useDict().app.scan;
  const plan = usePlan();
  const allowedModes = (plan.features.allowed_scan_modes as unknown as string[] | undefined) ?? ["common"];
  const maxTimeout = (plan.features.max_tool_timeout_seconds as unknown as number | undefined) ?? 300;

  const defaultTimeout = useMemo(() => {
    // Pick the largest preset that fits under the plan cap.
    const fits = TIMEOUTS.filter(([s]) => s <= maxTimeout).map(([s]) => s);
    return fits[fits.length - 1] ?? 300;
  }, [maxTimeout]);

  const [config, setConfig] = useState<ScanConfig>({
    portMode: "common",
    timeout: defaultTimeout,
    deepValidation: false,
  });

  const execute = useExecuteScan(engagement.id);
  const isScanning = engagement.status === "scanning";
  const showLive = isScanning || execute.isPending || execute.isSuccess;
  const dvEligible = Boolean(engagement.deep_validation_authorized);

  async function handleRun() {
    try {
      await execute.mutateAsync(config);
    } catch {
      // Surfaced via execute.error below
    }
  }

  const errorMsg =
    execute.error instanceof ApiError
      ? String(execute.error.detail)
      : execute.error instanceof Error
        ? execute.error.message
        : null;

  return (
    <div className="flex flex-col gap-4">
      {!showLive && (
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.05em] mb-4">
            {t.config}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-[6px] text-[11px] text-muted font-mono">
                {t.portMode}
              </label>
              {PORT_MODE_KEYS.map((mode) => {
                const locked = !allowedModes.includes(mode);
                return (
                  <label
                    key={mode}
                    className="flex items-center gap-[6px] mb-[6px] font-mono text-[12px]"
                    style={{
                      color: locked
                        ? "rgb(var(--color-dim))"
                        : config.portMode === mode
                          ? "rgb(var(--color-accent))"
                          : "rgb(var(--color-muted))",
                      cursor: locked ? "not-allowed" : "pointer",
                    }}
                    title={locked ? fmt(t.requiresHigher, { plan: plan.name }) : undefined}
                  >
                    <input
                      type="radio"
                      name="portMode"
                      checked={config.portMode === mode}
                      disabled={locked}
                      onChange={() =>
                        !locked && setConfig((c) => ({ ...c, portMode: mode }))
                      }
                      style={{ accentColor: "rgb(var(--color-accent))" }}
                    />
                    {t.portModes[mode]}
                    {locked && (
                      <span className="ml-1 text-[9px] uppercase tracking-wider text-muted">
                        Pro+
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            <div>
              <label className="block mb-[6px] text-[11px] text-muted font-mono">
                {t.phaseBudget}
              </label>
              <div className="font-mono text-[10px] text-muted leading-[1.4] mb-[8px]">
                {t.phaseBudgetHelp}
              </div>
              {TIMEOUTS.map(([seconds, requiredPlan]) => {
                const locked = seconds > maxTimeout;
                return (
                  <label
                    key={seconds}
                    className="flex items-center gap-[6px] mb-[6px] font-mono text-[11px]"
                    style={{
                      color: locked
                        ? "rgb(var(--color-dim))"
                        : config.timeout === seconds
                          ? "rgb(var(--color-accent))"
                          : "rgb(var(--color-muted))",
                      cursor: locked ? "not-allowed" : "pointer",
                    }}
                    title={locked ? fmt(t.availableOn, { plan: requiredPlan }) : undefined}
                  >
                    <input
                      type="radio"
                      name="timeout"
                      checked={config.timeout === seconds}
                      disabled={locked}
                      onChange={() =>
                        !locked && setConfig((c) => ({ ...c, timeout: seconds }))
                      }
                      style={{ accentColor: "rgb(var(--color-accent))" }}
                    />
                    {t.timeouts[String(seconds) as keyof typeof t.timeouts]}
                    {locked && requiredPlan && (
                      <span className="ml-1 text-[9px] uppercase tracking-wider text-muted">
                        {requiredPlan}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Deep validation (active-attack option — visually distinct) */}
          <div
            className="mt-4 px-[14px] py-3 rounded"
            style={{
              border: `1px solid ${dvEligible ? "rgb(var(--color-danger) / 0.4)" : "rgb(var(--color-stroke))"}`,
              background: dvEligible ? "rgb(var(--color-danger) / 0.039)" : "rgb(var(--color-bg2))",
            }}
          >
            <div className="flex items-start gap-[10px]">
              <input
                type="checkbox"
                id="dv-cb"
                checked={config.deepValidation}
                disabled={!dvEligible}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, deepValidation: e.target.checked }))
                }
                style={{
                  marginTop: "3px",
                  accentColor: "rgb(var(--color-danger))",
                  cursor: dvEligible ? "pointer" : "not-allowed",
                }}
              />
              <div className="flex-1 min-w-0">
                <label
                  htmlFor="dv-cb"
                  className="block font-mono text-[11px] font-bold uppercase tracking-[0.05em] mb-1"
                  style={{
                    color: dvEligible ? "rgb(var(--color-danger))" : "rgb(var(--color-dim))",
                    cursor: dvEligible ? "pointer" : "not-allowed",
                  }}
                >
                  {t.deepValidation}
                </label>
                <div className="font-mono text-[10px] text-muted leading-[1.5]">
                  {dvEligible ? t.dvEnabled : t.dvDisabled}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <Button
              variant="primary"
              onClick={handleRun}
              disabled={execute.isPending}
            >
              {t.execute}
            </Button>
            {config.deepValidation && (
              <div className="font-mono text-[10px] text-danger font-bold">
                {t.dvArmed}
              </div>
            )}
          </div>
        </Card>
      )}

      {showLive && <LiveProgress engagementId={engagement.id} />}

      {execute.isSuccess && execute.data && (
        <Card className="border-accent">
          <div className="font-mono text-[11px] text-accent uppercase tracking-[0.05em] mb-2">
            {t.queued}
          </div>
          <div className="font-sans text-[13px] text-content mb-1">
            {fmt(t.queuedMsg, { n: execute.data.display_number, priority: execute.data.priority })}
          </div>
          <div className="font-mono text-[11px] text-muted">
            {t.queuedHint}
          </div>
        </Card>
      )}

      {errorMsg && (
        <Card className="border-danger">
          <div className="font-mono text-[13px] text-danger">{t.error} {errorMsg}</div>
        </Card>
      )}

    </div>
  );
}
